// src/utils/initTracking.ts
import { trackingConfig } from '@/config/tracking'
import { env } from '@/utils/env'
import { setTrustedScriptSrc } from '@/utils/trustedScriptSrc'

interface Consent {
  categories: string[]
}

/**
 * Initializes Google, Ads, and Facebook Pixel based on consent.
 * Idempotent: does not try to load scripts again if they already exist.
 */
export function initTracking(consent: Consent | null) {
  const categories = consent?.categories || []
  const hasMarketing = categories.includes('marketing')
  const hasAnalytics = categories.includes('analytics')

  // Debug in dev and disabled tracking
  if (!trackingConfig.isEnabled || env.isDev) {
    console.groupCollapsed('[Tracking:debug]')
    console.info('Environment:', env.appEnv)
    console.info('Consent categories:', categories)
    console.info('Analytics allowed:', hasAnalytics)
    console.info('Marketing allowed:', hasMarketing)
    console.groupEnd()
    return
  }

  /** Google Ads (conversion tracking) */
  if (hasMarketing && trackingConfig.googleAds.convertion) {
    window.gtag?.('config', trackingConfig.googleAds.convertion)
  }

  /** Google Analytics (GA4) */
  if (hasAnalytics && trackingConfig.googleAnalytics.measurementId) {
    const id = trackingConfig.googleAnalytics.measurementId
    if (!window.gtag) {
      (function (w: any, d: Document, s: string, i: string) {
        w.dataLayer = w.dataLayer || []
        function gtag(...args: any[]) {
          w.dataLayer.push(args)
        }
        w.gtag = gtag
        gtag('js', new Date())
        gtag('config', i, { anonymize_ip: true })

        // Only add the script if it does not already exist
        if (!d.querySelector(`script[src*="gtag/js?id=${i}"]`)) {
          const script = d.createElement(s) as HTMLScriptElement
          script.async = true
          setTrustedScriptSrc(script, `https://www.googletagmanager.com/gtag/js?id=${i}`)
          d.head.appendChild(script)
        }
      })(window, document, 'script', id)
    }
  }

  /** Facebook Pixel */
  if (hasMarketing && trackingConfig.facebookPixel.id) {
    ((f: any, b: Document, e: string, v: string) => {
      if (f.fbq) return

      const n: any = function (...args: any[]) {
        if (n.callMethod) n.callMethod(...args)
        else n.queue.push(args)
      }

      f.fbq = n
      f._fbq = n
      n.push = n
      n.loaded = true
      n.version = '2.0'
      n.queue = []

      // Use the correct type for the script
      if (!b.querySelector(`script[src*="connect.facebook.net/en_US/fbevents.js"]`)) {
        const t = b.createElement(e) as HTMLScriptElement
        t.async = true
        setTrustedScriptSrc(t, v)
        const s = b.getElementsByTagName(e)[0]
        if (s?.parentNode) {
          s.parentNode.insertBefore(t, s)
        } else {
          b.head.appendChild(t)
        }
      }

      n('init', trackingConfig.facebookPixel.id)
      n('track', 'PageView')
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  }
}
